import React from "react";
import { Link } from "react-router-dom";
import { Accessibility, BedDouble, Footprints, Zap, TrendingUp, Armchair, Heart } from "lucide-react";
import { isGestosAgeisDomain } from "@/lib/siteDomain";

const LANGS = ['pt', 'en', 'es', 'fr', 'it', 'de'];

// Internal linking section — passes authority from landing pages to dedicated
// equipment SEO pages (key for ranking "cadeira de rodas lisboa" etc.).
const POPULAR_EQUIPMENT = [
  { slug: 'cadeira-de-rodas', icon: Accessibility, price: '5€', name: { pt: 'Cadeira de Rodas', en: 'Wheelchair', es: 'Silla de Ruedas', fr: 'Fauteuil Roulant', it: 'Carrozzella', de: 'Rollstuhl' } },
  { slug: 'cadeira-de-rodas-eletrica', icon: Zap, price: '25€', name: { pt: 'Cadeira de Rodas Elétrica', en: 'Electric Wheelchair', es: 'Silla de Ruedas Eléctrica', fr: 'Fauteuil Roulant Électrique', it: 'Carrozzella Elettrica', de: 'Elektrischer Rollstuhl' } },
  { slug: 'cadeira-de-rodas-para-escadas', icon: TrendingUp, price: '8€', name: { pt: 'Cadeira para Escadas', en: 'Stair Wheelchair', es: 'Silla para Escaleras', fr: 'Fauteuil pour Escaliers', it: 'Carrozzella per Scale', de: 'Treppenrollstuhl' } },
  { slug: 'cadeira-de-rodas-de-conforto', icon: Armchair, price: '6€', name: { pt: 'Cadeira de Conforto', en: 'Comfort Wheelchair', es: 'Silla de Confort', fr: 'Fauteuil de Confort', it: 'Carrozzella da Conforto', de: 'Komfort-Rollstuhl' } },
  { slug: 'cadeira-de-rodas-eletrica-dobravel', icon: Zap, price: '18€', name: { pt: 'Elétrica Dobrável', en: 'Folding Electric', es: 'Eléctrica Plegable', fr: 'Électrique Pliable', it: 'Elettrica Pieghevole', de: 'Klappbarer Elektrorollstuhl' } },
  { slug: 'cama-articulada', icon: BedDouble, price: '15€', name: { pt: 'Cama Articulada', en: 'Hospital Bed', es: 'Cama Articulada', fr: 'Lit Articulé', it: 'Letto Articolato', de: 'Krankenbett' } },
  { slug: 'andarilho', icon: Footprints, price: '3€', name: { pt: 'Andarilho', en: 'Walker', es: 'Andador', fr: 'Déambulateur', it: 'Deambulatore', de: 'Rollator' } },
  { slug: 'scooter-mobilidade', icon: Accessibility, price: '25€', name: { pt: 'Scooter de Mobilidade', en: 'Mobility Scooter', es: 'Scooter de Movilidad', fr: 'Scooter de Mobilité', it: 'Scooter per Mobilità', de: 'Mobilitätsscooter' } },
];

const POPULAR_CITIES = ['lisboa', 'porto', 'cascais', 'sintra', 'coimbra', 'braga', 'faro'];

const CITY_NAMES = {
  lisboa: { pt: 'Lisboa', en: 'Lisbon', es: 'Lisboa', fr: 'Lisbonne', it: 'Lisbona', de: 'Lissabon' },
  porto: { pt: 'Porto', en: 'Porto', es: 'Porto', fr: 'Porto', it: 'Porto', de: 'Porto' },
  cascais: { pt: 'Cascais', en: 'Cascais', es: 'Cascais', fr: 'Cascais', it: 'Cascais', de: 'Cascais' },
  sintra: { pt: 'Sintra', en: 'Sintra', es: 'Sintra', fr: 'Sintra', it: 'Sintra', de: 'Sintra' },
  coimbra: { pt: 'Coimbra', en: 'Coimbra', es: 'Coímbra', fr: 'Coimbra', it: 'Coimbra', de: 'Coimbra' },
  braga: { pt: 'Braga', en: 'Braga', es: 'Braga', fr: 'Braga', it: 'Braga', de: 'Braga' },
  faro: { pt: 'Faro', en: 'Faro', es: 'Faro', fr: 'Faro', it: 'Faro', de: 'Faro' },
};

const strings = {
  pt: {
    title: 'Equipamento popular para alugar',
    subtitle: 'Alugue online com entrega 24h em todo Portugal — Lisboa, Porto e todo o país.',
    fromDay: (price) => `desde ${price}/dia`,
    citiesTitle: 'Entrega nas principais cidades',
    cityPill: (city) => `Cadeira de Rodas ${city}`,
  },
  en: {
    title: 'Popular equipment for rent',
    subtitle: 'Rent online with 24h delivery across Portugal — Lisbon, Porto and nationwide.',
    fromDay: (price) => `from ${price}/day`,
    citiesTitle: 'Delivery in major cities',
    cityPill: (city) => `Wheelchair ${city}`,
  },
  es: {
    title: 'Equipo popular para alquilar',
    subtitle: 'Alquile online con entrega 24h en todo Portugal — Lisboa, Porto y todo el país.',
    fromDay: (price) => `desde ${price}/día`,
    citiesTitle: 'Entrega en las principales ciudades',
    cityPill: (city) => `Silla de Ruedas ${city}`,
  },
  fr: {
    title: 'Matériel populaire à louer',
    subtitle: 'Louez en ligne avec livraison 24h dans tout le Portugal — Lisbonne, Porto et tout le pays.',
    fromDay: (price) => `dès ${price}/jour`,
    citiesTitle: 'Livraison dans les principales villes',
    cityPill: (city) => `Fauteuil Roulant ${city}`,
  },
  it: {
    title: 'Attrezzatura popolare da noleggiare',
    subtitle: 'Noleggia online con consegna 24h in tutto il Portogallo — Lisbona, Porto e tutto il paese.',
    fromDay: (price) => `da ${price}/giorno`,
    citiesTitle: 'Consegna nelle principali città',
    cityPill: (city) => `Carrozzella ${city}`,
  },
  de: {
    title: 'Beliebte Mietgeräte',
    subtitle: 'Online mieten mit 24h-Lieferung in ganz Portugal — Lissabon, Porto und im ganzen Land.',
    fromDay: (price) => `ab ${price}/Tag`,
    citiesTitle: 'Lieferung in den wichtigsten Städten',
    cityPill: (city) => `Rollstuhl ${city}`,
  },
};

export default function PopularEquipmentLinks({ language = 'pt' }) {
  const lang = LANGS.includes(language) ? language : 'pt';
  const s = strings[lang];
  const isGestos = isGestosAgeisDomain();
  // Domain-aware brand palette — green for Gestos Ágeis, navy for Orthocare.
  const brand = isGestos ? '#0e6b4f' : '#0a3452';
  const brandDark = isGestos ? '#0a4a37' : '#061f35';
  // Both domains share the same /aluguer/ routes; canonical stays per-domain inside EquipmentSEOPage
  const basePath = '/aluguer';

  return (
    <section className="py-10 sm:py-12 px-4 bg-slate-50">
      <style>{`
        .pel-card:hover { background-color: ${brand}0d; border-color: ${brand}66; }
        .pel-card:hover .pel-name { color: ${brand}; }
        .pel-city:hover { color: ${brandDark}; border-color: ${brand}66; background-color: ${brand}0d; }
      `}</style>
      <div className="max-w-5xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2 text-center">
          {s.title}
        </h2>
        <p className="text-center text-slate-500 text-sm mb-6">
          {s.subtitle}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
          {POPULAR_EQUIPMENT.map((eq) => {
            const Icon = eq.icon;
            return (
              <Link
                key={eq.slug}
                to={`${basePath}/${eq.slug}`}
                className="pel-card bg-white border border-slate-200 rounded-xl p-3 sm:p-4 transition group flex flex-col items-center text-center"
              >
                <Icon className="w-6 h-6 sm:w-7 sm:h-7 mb-2" style={{ color: brand }} />
                <p className="pel-name text-sm font-semibold text-slate-800 leading-tight">
                  {eq.name[lang]}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{s.fromDay(eq.price)}</p>
              </Link>
            );
          })}
        </div>

        <h3 className="text-base font-bold text-slate-700 mb-3 text-center">
          {s.citiesTitle}
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {POPULAR_CITIES.map((city) => (
            <Link
              key={city}
              to={`${basePath}/cadeira-de-rodas/${city}`}
              className="pel-city text-sm bg-white border border-slate-200 rounded-full px-3 py-1.5 capitalize transition"
              style={{ color: brand }}
            >
              {s.cityPill(CITY_NAMES[city][lang])}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}